import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { TemplateRef } from '@angular/core';
import { Component, computed, input, model, output, signal, inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, take } from 'rxjs';

import { NgTemplateAssertType } from '@core/common-legacy';
import { DepartmentActions, DepartmentSelectors } from '@core/department-data';
import type { Department } from '@core/departments-types';
import type { Attribute, AttributeListData } from '@core/http-types';
import type { ListTemplateContext } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  OverlayOriginDirective,
  SelectComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import { DepartmentSelectComponent } from '../../components/department-select/department-select.component';
import type { DepartmentAttribute } from '../../types/departments.types';

@Component({
  selector: 'rt-department-select-container',
  templateUrl: 'department-select-container.component.html',
  imports: [
    DepartmentSelectComponent,
    SelectComponent,
    ButtonDropdownIndicatorDirective,
    ButtonComponent,
    OverlayOriginDirective,
    NgTemplateAssertType,
    TruncateComponent,
  ],
})
export class DepartmentSelectContainerComponent {
  public static readonly emptyItemId = -1;

  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  public readonly TriggerTemplateContext: ListTemplateContext;

  public selectedIds = input<number[]>([]);
  public excludedIds = input<number[]>([]);
  public canCreate = input(false);
  public langCode = input<string | null>(null);
  public dropdownPositions = input<ConnectionPositionPair[] | null>(null);
  public listTpl = input<TemplateRef<ListTemplateContext>>();
  public selectFooterTemplate = input<TemplateRef<any> | null>(null);
  public selectHeaderTemplate = input<TemplateRef<any> | null>(null);
  public toggleDropdown = model(false);
  public isReadOnly = input(false);
  public header = input<string | null>(null);
  public hasTriggerWidth = input(true);
  public multiSelect = input(true);
  public emptyOptionText = input<string | null>(null);
  public maxTriggerWidth = input<number | null>(null);

  public selectDepartment = output<DepartmentAttribute>();
  public remove = output<DepartmentAttribute>();
  public goToSettings = output<void>();

  public query = signal('');
  public isLoaded = this.store.selectSignal(DepartmentSelectors.isLoaded());
  public isPending = this.store.selectSignal(DepartmentSelectors.isPending());
  public departmentsData = this.store.selectSignal(DepartmentSelectors.entities());

  public collection = computed(() => {
    const emptyOptionText = this.emptyOptionText();
    const langCode = this.langCode();
    const data = this.departmentsData().map(department => ({
      id: department.id,
      value: this.resolveTranslation(department, langCode),
    }));
    return !this.multiSelect() && emptyOptionText
      ? this.prependEmptyOption(data, emptyOptionText)
      : data;
  });
  public selected = computed(() =>
    this.collection().filter(attribute => this.selectedIds().includes(attribute.id)),
  );
  public suggestions = computed((): AttributeListData => {
    const searchValue = this.query().toLowerCase();
    const filtered = this.collection().filter(({ value }) =>
      value.toLowerCase().includes(searchValue),
    );
    const rows = filtered.filter(attribute => !this.excludedIds().includes(attribute.id));
    return {
      isLoading: false,
      meta: {
        page: 1,
        limit: rows.length,
        totalCount: rows.length,
      },
      rows,
    };
  });

  public ngOnInit() {
    if (!(this.isLoaded() || this.isPending())) {
      this.store.dispatch(DepartmentActions.fetchCollection());
    }
  }

  public selectOrCreate(department: Attribute) {
    if (department.id) {
      this.selectDepartment.emit(<DepartmentAttribute>department);
      this.toggleDropdown.update(v => !v);
      return;
    }

    if (!this.canCreate()) return;

    const uuid = generateUuid();
    this.store.dispatch(
      DepartmentActions.create({
        payload: { data: { name: department.value }, correlationId: uuid },
      }),
    );
    this.onCreateSuccess(uuid).subscribe(({ id, name }) => {
      this.selectDepartment.emit({ id, value: name });
    });
  }

  private resolveTranslation(department: Department, langCode: string | null): string {
    return langCode
      ? department.groupedTranslations[langCode]?.name || department.name
      : department.name;
  }

  private prependEmptyOption(
    collection: DepartmentAttribute[],
    text: string,
  ): DepartmentAttribute[] {
    return [{ id: DepartmentSelectContainerComponent.emptyItemId, value: text }, ...collection];
  }

  private onCreateSuccess(uuid: string) {
    return this.actions$.pipe(
      ofType(DepartmentActions.createSuccess),
      filter(({ payload }) => uuid === payload.correlationId),
      map(({ payload }) => payload.data),
      take(1),
    );
  }
}
